import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyChart } from 'components/SurveyChart';
import { SURVEY_TYPES } from 'constants/surveys';
import type { Survey } from 'types/surveys';
import { DEFAULT_SURVEY_TAG, getTagIcon, getTagTitleKey, getTagIconColor } from './utils';

interface TestCardProps {
  survey: Survey;
  onStartTest: (surveyId: string) => void;
}

export const TestCard = ({ survey, onStartTest }: TestCardProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const [isStarting, setIsStarting] = useState(false);
  const tag = survey.tag ?? DEFAULT_SURVEY_TAG;
  const Icon = getTagIcon(tag);
  const titleKey = getTagTitleKey(tag);
  const title = t(titleKey);
  const iconColor = getTagIconColor(tag);
  const buttonText = `Realizar test de ${title}`;

  const handleStartTest = (): void => {
    if (isStarting) return;
    setIsStarting(true);
    onStartTest(survey.id);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6 w-full">
      <div className="flex items-center gap-2 mb-3">
        <div
          className={cn(
            'w-10 h-10 shrink-0 rounded-full flex items-center justify-center',
            iconColor.bg
          )}
        >
          <Icon className={cn('w-5 h-5', iconColor.text)} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="flex items-center gap-1 mb-4">
        <TrendingUp className="w-4 h-4 text-gray-500" />
        <p className="text-sm text-gray-600">{t('WellBeing.Test.monthlyEvolution.heading')}</p>
      </div>
      <div className="mb-4">
        <SurveyChart surveyType={SURVEY_TYPES.TEST} surveyId={survey.id} showFilters={true} />
      </div>
      <Button className="w-full" onClick={handleStartTest} disabled={isStarting}>
        {buttonText}
      </Button>
    </div>
  );
};
